import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ShieldCheck, Mail, Phone, CheckCircle2, AlertTriangle,
  Loader2, Sparkles,
} from "lucide-react";

/**
 * VendorVerify — the lightweight cousin of OrganizerVerify.
 *
 * Vendors get 2 layers (email + phone). Completing both flips a
 * "Trust-Verified" badge on booking dialogs + vendor cards so
 * private-party booking buyers see extra signals when they book.
 * No legal name / gov ID required — the friction isn't worth it for
 * makers selling craft goods.
 */
function StepCard({ testId, done, active, title, subtitle, icon: Icon, accent, children }) {
  const borderColor = done ? "rgba(132,204,22,0.5)"
    : active ? "var(--avt-purple)" : "rgba(0,0,0,0.1)";
  return (
    <div data-testid={testId}
         data-verify-done={done ? "true" : "false"}
         className="rounded-2xl border-2 p-5 sm:p-6 transition"
         style={{ borderColor, background: done ? "rgba(132,204,22,0.06)" : "#fff" }}>
      <div className="flex items-start gap-4">
        <span className="rounded-full p-3 flex-shrink-0"
              style={{ backgroundColor: `${accent}18`, color: accent }}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif text-xl">{title}</h3>
            {done && (
              <span data-testid={`${testId}-done-badge`}
                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                    style={{ backgroundColor: "rgba(132,204,22,0.15)", color: "#4B7A0A" }}>
                <CheckCircle2 className="h-3 w-3" /> Done
              </span>
            )}
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>{subtitle}</p>
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function VendorVerify() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneStarted, setPhoneStarted] = useState(false);
  const [otp, setOtp] = useState("");

  const reload = async () => {
    try {
      const { data } = await api.get("/vendor/verify/status");
      setStatus(data);
      if (data.phone.value) setPhoneInput(data.phone.value);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load status");
    }
  };

  useEffect(() => {
    (async () => { await reload(); setLoading(false); })();
  }, []);

  const setB = (k, v) => setBusy((s) => ({ ...s, [k]: v }));

  const sendEmail = async () => {
    setB("email", true);
    try {
      await api.post("/vendor/verify/email/send");
      toast.success("Check your inbox — link expires in 24 hours");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send email");
    } finally { setB("email", false); }
  };

  const startPhone = async () => {
    if (!phoneInput.trim()) return;
    setB("phone-start", true);
    try {
      await api.post("/vendor/verify/phone/start", { phone: phoneInput.trim() });
      setPhoneStarted(true);
      toast.success("Code sent — expires in 10 minutes");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send code");
    } finally { setB("phone-start", false); }
  };

  const checkPhone = async () => {
    if (!/^\d{4,10}$/.test(otp)) { toast.error("Enter the code you got"); return; }
    setB("phone-check", true);
    try {
      await api.post("/vendor/verify/phone/check", { code: otp });
      toast.success("Phone verified");
      setPhoneStarted(false);
      setOtp("");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Wrong or expired code");
    } finally { setB("phone-check", false); }
  };

  if (!user || user.role !== "vendor") {
    return (
      <div className="mx-auto max-w-2xl px-6 py-12 text-center">
        <p style={{ color: "var(--ink-soft)" }}>Vendor accounts only.</p>
      </div>
    );
  }
  if (loading || !status) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: "var(--avt-purple)" }} />
      </div>
    );
  }

  const emailDone = status.email.verified;
  const phoneDone = status.phone.verified;
  const doneCount = [emailDone, phoneDone].filter(Boolean).length;
  const allDone = status.trust_verified;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10 py-10" data-testid="vendor-verify">
      <div className="flex items-center gap-3">
        <span className="rounded-full p-3"
              style={{ backgroundColor: "rgba(107,47,186,0.14)", color: "var(--avt-purple)" }}>
          <ShieldCheck className="h-6 w-6" />
        </span>
        <div>
          <div className="overline" style={{ color: "var(--avt-purple)" }}>Vendor Trust</div>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
            {allDone ? <>You&rsquo;re a <span style={{ color: "#4B7A0A" }}>Trust-Verified vendor.</span></>
              : <>Build buyer trust in 2 steps.</>}
          </h1>
        </div>
      </div>
      <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
        Confirm your email + phone and get a green Trust-Verified pill on
        private-party booking dialogs so buyers know they&rsquo;re dealing with a real person.
      </p>

      <div className="mt-6 rounded-full h-2 overflow-hidden"
           style={{ backgroundColor: "rgba(107,47,186,0.12)" }} data-testid="vendor-verify-progress">
        <div className="h-full transition-all"
             data-testid="vendor-verify-progress-fill"
             style={{
               width: `${(doneCount / 2) * 100}%`,
               background: allDone
                 ? "linear-gradient(90deg, #84CC16, #22c55e)"
                 : "linear-gradient(90deg, #6B2FBA, #F97316)",
             }} />
      </div>
      <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>{doneCount} of 2 complete</div>

      {allDone && (
        <div className="mt-6 rounded-xl p-4 border-2"
             data-testid="vendor-verify-done"
             style={{ borderColor: "rgba(132,204,22,0.5)", backgroundColor: "rgba(132,204,22,0.08)" }}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" style={{ color: "#4B7A0A" }} />
            <span className="font-serif text-lg" style={{ color: "#4B7A0A" }}>You&rsquo;re Trust-Verified.</span>
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
            The Trust-Verified pill is now live on every booking dialog that mentions you.
          </p>
        </div>
      )}

      <div className="mt-8 space-y-4">
        <StepCard testId="vendor-verify-step-email" done={emailDone} active={!emailDone}
                  title="1. Confirm your email"
                  subtitle={`We'll send a one-time link to ${status.email.value}.`}
                  icon={Mail} accent="var(--avt-teal)">
          {emailDone ? (
            <div className="text-sm" style={{ color: "#4B7A0A" }}>
              Verified{status.email.verified_at ? ` on ${new Date(status.email.verified_at).toLocaleDateString()}` : ""}.
            </div>
          ) : (
            <Button onClick={sendEmail} disabled={busy["email"]} data-testid="vendor-verify-email-send"
                    className="btn-primary rounded-full px-5 py-2.5">
              {busy["email"] ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send confirmation link"}
            </Button>
          )}
        </StepCard>

        <StepCard testId="vendor-verify-step-phone" done={phoneDone} active={emailDone && !phoneDone}
                  title="2. Verify your phone"
                  subtitle="We'll text a 6-digit code. Standard SMS rates apply."
                  icon={Phone} accent="var(--avt-orange)">
          {!status.providers.phone_configured && (
            <div className="text-xs rounded-md px-3 py-2 inline-flex items-center gap-2"
                 style={{ backgroundColor: "rgba(249,115,22,0.10)", color: "var(--avt-orange)" }}>
              <AlertTriangle className="h-3 w-3" /> Phone verification isn&rsquo;t configured yet — check back soon.
            </div>
          )}
          {phoneDone ? (
            <div className="text-sm" style={{ color: "#4B7A0A" }}>Verified {status.phone.value}</div>
          ) : status.providers.phone_configured && (
            <>
              {!phoneStarted ? (
                <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                  <div className="flex-1">
                    <Label className="text-[10px] uppercase tracking-widest">Phone (include country code)</Label>
                    <Input data-testid="vendor-verify-phone-input" value={phoneInput}
                           onChange={(e) => setPhoneInput(e.target.value)}
                           placeholder="+14155552671" autoComplete="tel" className="mt-1" />
                  </div>
                  <Button onClick={startPhone} disabled={busy["phone-start"] || !phoneInput.trim()}
                          data-testid="vendor-verify-phone-send"
                          className="btn-primary rounded-full px-5 py-2.5">
                    {busy["phone-start"] ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send code"}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                  <div className="flex-1">
                    <Label className="text-[10px] uppercase tracking-widest">6-digit code</Label>
                    <Input data-testid="vendor-verify-otp-input" value={otp} inputMode="numeric"
                           autoComplete="one-time-code" maxLength={6}
                           onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                           className="mt-1" />
                  </div>
                  <Button onClick={checkPhone} disabled={busy["phone-check"]}
                          data-testid="vendor-verify-otp-check"
                          className="btn-primary rounded-full px-5 py-2.5">
                    {busy["phone-check"] ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify code"}
                  </Button>
                  <button type="button" onClick={() => setPhoneStarted(false)}
                          data-testid="vendor-verify-phone-retry"
                          className="text-xs underline" style={{ color: "var(--ink-soft)" }}>
                    Use a different number
                  </button>
                </div>
              )}
            </>
          )}
        </StepCard>
      </div>
    </div>
  );
}
